import { businessService } from '@/pages/Business/services/business.service';
import { API_INTEGRATION_TEST_BUSINESS_URL } from '@/utils';
import type { PaginatedResponse } from '@/types';

import type {
  IntegrationImportResult,
  IntegrationPackage,
  IntegrationRequestStep,
  IntegrationStepRecord,
} from '../models/IntegrationRun';

import type {
  IntegrationRequest,
  IntegrationRequestDetail,
  IntegrationRequestFormOptions,
  IntegrationRequestPayload,
  IntegrationRequestStatus,
  IntegrationRequestType,
} from '../models/IntegrationRequest';

interface IntegrationRunResult {
  dryRun: boolean;
  steps: IntegrationRequestStep[];
  request: IntegrationRequestDetail | null;
}

const integrationRequestsApi = businessService.injectEndpoints({
  endpoints: (builder) => ({
    getIntegrationRequests: builder.query<
      PaginatedResponse<IntegrationRequest>,
      {
        statuses?: IntegrationRequestStatus[];
        type?: IntegrationRequestType | '';
        commerceId?: string;
        page: number;
        pageSize: number;
      }
    >({
      query: ({ statuses, type, commerceId, page, pageSize }) => ({
        url: '/integration-requests',
        params: {
          limit: pageSize,
          offset: (page - 1) * pageSize,
          ...(statuses?.length ? { status: statuses.join(',') } : {}),
          ...(type && { type }),
          ...(commerceId && { commerceId }),
        },
      }),
      providesTags: ['IntegrationRequests'],
    }),

    getIntegrationRequestById: builder.query<IntegrationRequestDetail, string>({
      query: (requestId) => ({ url: `/integration-requests/${requestId}` }),
      providesTags: ['IntegrationRequests'],
    }),

    getIntegrationRequestFormOptions: builder.query<IntegrationRequestFormOptions, string>({
      query: (commerceId) => ({
        url: '/integration-requests/form-options',
        params: { commerceId },
      }),
    }),

    createIntegrationRequest: builder.mutation<IntegrationRequestDetail, IntegrationRequestPayload>({
      query: (body) => ({ url: '/integration-requests', method: 'POST', body }),
      invalidatesTags: ['IntegrationRequests'],
    }),

    updateIntegrationRequest: builder.mutation<
      IntegrationRequestDetail,
      { requestId: string; payload: IntegrationRequestPayload }
    >({
      query: ({ requestId, payload }) => ({
        url: `/integration-requests/${requestId}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['IntegrationRequests'],
    }),

    cancelIntegrationRequest: builder.mutation<IntegrationRequestDetail, string>({
      query: (requestId) => ({
        url: `/integration-requests/${requestId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['IntegrationRequests'],
    }),

    observeIntegrationRequest: builder.mutation<
      IntegrationRequestDetail,
      { requestId: string; observation: string }
    >({
      query: ({ requestId, observation }) => ({
        url: `/integration-requests/${requestId}/observe`,
        method: 'POST',
        body: { observation },
      }),
      invalidatesTags: ['IntegrationRequests'],
    }),

    approveIntegrationRequestDoc: builder.mutation<IntegrationRequestDetail, string>({
      query: (requestId) => ({
        url: `/integration-requests/${requestId}/approve-doc`,
        method: 'POST',
      }),
      invalidatesTags: ['IntegrationRequests'],
    }),

    markIntegrationRequestSent: builder.mutation<IntegrationRequestDetail, string>({
      query: (requestId) => ({
        url: `/integration-requests/${requestId}/mark-sent`,
        method: 'POST',
      }),
      invalidatesTags: ['IntegrationRequests'],
    }),

    integrateIntegrationRequest: builder.mutation<
      IntegrationRunResult,
      { requestId: string; dryRun: boolean }
    >({
      query: ({ requestId, dryRun }) => ({
        url: `/integration-requests/${requestId}/integrate`,
        method: 'POST',
        body: { dryRun },
      }),
      invalidatesTags: (result) => (result?.dryRun ? [] : ['IntegrationRequests']),
    }),

    recordIntegrationRequestStep: builder.mutation<
      IntegrationRequestDetail,
      { requestId: string; step: IntegrationStepRecord }
    >({
      query: ({ requestId, step }) => ({
        url: `/integration-requests/${requestId}/steps`,
        method: 'POST',
        body: step,
      }),
      invalidatesTags: ['IntegrationRequests'],
    }),

    exportIntegrationPackage: builder.mutation<IntegrationPackage, string>({
      query: (requestId) => ({ url: `/integration-requests/${requestId}/export` }),
    }),

    importIntegrationPackageInTest: builder.mutation<
      IntegrationImportResult,
      { integrationPackage: IntegrationPackage; dryRun: boolean }
    >({
      query: ({ integrationPackage, dryRun }) => ({
        url: `${API_INTEGRATION_TEST_BUSINESS_URL}/integration-requests/sync/import`,
        method: 'POST',
        body: { package: integrationPackage, dryRun },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetIntegrationRequestsQuery,
  useGetIntegrationRequestByIdQuery,
  useGetIntegrationRequestFormOptionsQuery,
  useCreateIntegrationRequestMutation,
  useUpdateIntegrationRequestMutation,
  useCancelIntegrationRequestMutation,
  useObserveIntegrationRequestMutation,
  useApproveIntegrationRequestDocMutation,
  useMarkIntegrationRequestSentMutation,
  useIntegrateIntegrationRequestMutation,
  useRecordIntegrationRequestStepMutation,
  useExportIntegrationPackageMutation,
  useImportIntegrationPackageInTestMutation,
} = integrationRequestsApi;
